import React from 'react';
import { Info, ListChecks, RefreshCw } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
import { NeedsFlag } from '../ui/NeedsFlag';
type ItVerdict = 'Permitted' | 'Permitted with conditions';
type ItClaim = {
  text: string;
  classification: 'Health' | 'Presentation';
  verdict: ItVerdict;
  references: {
    text: string;
    url?: string;
  }[];
  ruleText?: string;
  recommendation: string;
  flag?: string;
};
const REG_432 = {
  text: 'Regulation (EU) No 432/2012',
  url: 'https://eur-lex.europa.eu/eli/reg/2012/432/oj/eng'
};
const REG_1924 = {
  text: 'Regulation (EC) No 1924/2006',
  url: 'https://eur-lex.europa.eu/eli/reg/2006/1924/oj/eng'
};
const REG_1169 = {
  text: 'Regulation (EU) No 1169/2011, Annex XIII',
  url: 'https://eur-lex.europa.eu/eli/reg/2011/1169/oj/eng'
};
const REG_1169_ART7 = {
  text: 'Regulation (EU) No 1169/2011, Article 7',
  url: 'https://eur-lex.europa.eu/eli/reg/2011/1169/oj/eng'
};
const claims: ItClaim[] = [{
  text: 'With vitamin C for collagen formation and the normal function of blood vessels (veins) as well as copper and manganese to protect cells from oxidative stress',
  classification: 'Health',
  verdict: 'Permitted with conditions',
  references: [REG_1924, REG_432, REG_1169],
  ruleText: 'Only health claims listed in the Union Register of authorised claims may be made, and they may be used only where the food meets the conditions of use set out for that claim.',
  recommendation: "The underlying health relationships for vitamin C, copper and manganese are authorised. Remove '(veins)' and retain wording consistent with the authorised claims. Copper and manganese claim eligibility should be confirmed against the applicable 'source of' condition.",
  flag: 'Needs: confirmation of the copper and manganese source-of thresholds'
}, {
  text: 'With thiamine (vitamin B1) for normal heart function',
  classification: 'Health',
  verdict: 'Permitted',
  references: [REG_1924, REG_432],
  ruleText: 'Thiamine contributes to the normal function of the heart.',
  recommendation: "Thiamine's contribution to normal heart function is an authorised EU health claim. For Italy, use the corresponding authorised Italian-language wording."
}, {
  text: "Premium active ingredients in their purest form, sourced directly from the world's best producers",
  classification: 'Presentation',
  verdict: 'Permitted with conditions',
  references: [REG_1169_ART7],
  ruleText: 'Food information shall not be misleading, and shall be accurate, clear and easy to understand for the consumer.',
  recommendation: "'Purest form' and 'world's best producers' are objective superiority and quality claims and require adequate supporting evidence. If unavailable, soften or remove these statements.",
  flag: 'Needs: substantiating evidence'
}];
export function ItalyClaimsTab() {
  return <div className="space-y-4" data-id="element-688" data-mp="2hyz5j27tlh8v">
      <header className="flex items-start gap-3" data-id="element-689" data-mp="2hvtjjvuhmvhd">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-690" data-mp="6auclo66240e">
          <ListChecks className="h-5 w-5" data-id="element-691" data-mp="1aojs4o5ubgq" />
        </span>
        <div data-id="element-692" data-mp="3shit0pt0qm8w">
          <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-693" data-mp="3sgzd1emxi7xi">Claims</h2>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-[#6b7d70]" data-id="element-694" data-mp="3nj7l4kmaaltb">
            Claims assessed against Regulation (EC) No 1924/2006 and the Italian national reference framework.
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-695" data-mp="k3a883wqmzra">
        <p className="text-sm text-[#718174]" data-id="element-696" data-mp="f9xy0yst2vi2">{claims.length} claims assessed · reviewed 17 Aug 2026</p>
        <button type="button" className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5] sm:self-auto" data-id="element-697" data-mp="abeboxaavhp6">
          <RefreshCw className="h-4 w-4" data-id="element-698" data-mp="nj3e18y1mhnt" />
          Re-analyse
        </button>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900" data-id="element-699" data-mp="shwqcy1lg2me">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-700" data-mp="dlxmy0pfiqnl" />
        <div data-id="element-701" data-mp="ij7997hium77">
          <p data-id="element-702" data-mp="iixllqjy8r4o">
            One claim needs a wording adjustment before use, and one needs substantiating evidence. The heart-health claim is ready to use as authorised
            EU wording.
          </p>
          <p className="mt-2" data-id="element-703" data-mp="1b7cx433vgrb9">
            Claims placed on the Italian market must appear in Italian. Where a claim is tied to a specific ingredient, use the validated Italian wording
            from the Annex rather than a local translation.
          </p>
          <div className="mt-2.5" data-id="element-704" data-mp="1p85ucvlk60i2">
            <RegulationLink text="REGOLAMENTO (UE) N. 432/2012 — Allegato (testo italiano)" href="https://eur-lex.europa.eu/legal-content/IT/TXT/PDF/?uri=CELEX:32012R0432" data-id="element-705" data-mp="1p74hclkzf3xp" />
          </div>
        </div>
      </div>

      {claims.map(claim => <article key={claim.text} className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-706" data-mp="39qq001sw3fkp">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-707" data-mp="3ofdvk82qjpan">
            <h3 className="font-[Figtree] text-lg font-semibold leading-snug text-evergreen" data-id="element-708" data-mp="3jfrfzrzfpwyo">{claim.text}</h3>
            <div className="flex shrink-0 items-center gap-2" data-id="element-709" data-mp="2vrhyv3d61xdv">
              <span className="rounded-md bg-[#f0f3ef] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#637467]" data-id="element-710" data-mp="2qrc7loat6cwb">
                {claim.classification}
              </span>
              <VerdictBadge verdict={claim.verdict} data-id="element-711" data-mp="30prvliejh61l" />
            </div>
          </div>
          <div className="mt-5" data-id="element-712" data-mp="k0w705er3b4y">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-713" data-mp="k04j7greahp8">Regulatory reference</p>
            <div className="mt-1.5 flex flex-wrap gap-2" data-id="element-714" data-mp="fyritm72n6pp">
              {claim.references.map(reference => <RegulationLink key={reference.text} text={reference.text} href={reference.url} data-id="element-715" data-mp="38vvn02sm26qo" />)}
            </div>
            {claim.ruleText && <blockquote className="mt-3 border-l-2 border-lime-glow pl-3 text-sm italic leading-relaxed text-[#617366]" data-id="element-716" data-mp="3ita4m5q9t1kk">“{claim.ruleText}”</blockquote>}
            {claim.flag && <div className="mt-3" data-id="element-717" data-mp="2q4cq8dp6jox3">
                <NeedsFlag data-id="element-718" data-mp="2q16zyv4tegzs">{claim.flag}</NeedsFlag>
              </div>}
            <div className="mt-4" data-id="element-719" data-mp="2v2mjgircgfg2">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-720" data-mp="2z3zmym6ys6jt">Recommendation</p>
              <p className="mt-1 text-sm leading-relaxed text-[#506355]" data-id="element-721" data-mp="33uy5lsffdgq8">{claim.recommendation}</p>
            </div>
          </div>
        </article>)}

      <div className="flex items-start gap-2 rounded-xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#607266]" data-id="element-722" data-mp="27az9bciaxj0h">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#718174]" data-id="element-723" data-mp="23tteigrrh50x" />
        Suggestions only — not a verification. Each claim must be confirmed and, where indicated, substantiated before use.
      </div>
    </div>;
}
function VerdictBadge({
  verdict
}: {
  verdict: ItVerdict;
}) {
  const style = verdict === 'Permitted' ? 'bg-forest-green/10 text-forest-green ring-forest-green/20' : 'bg-amber-100 text-amber-800 ring-amber-200';
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${style}`} data-id="element-724" data-mp="1kxbhbju58cxa">{verdict}</span>;
}